import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../utils/cn'
import { usePresence } from '../../hooks/usePresence'

// Panel/scrim enter+exit duration — matches --transition-base (200ms).
const MODAL_MS = 200

/**
 * Every modal currently open, outermost first.
 *
 * <p>Escape closes only the LAST one. Without this, a modal opened over
 * another — the service catalogue over the intake dialog, the close
 * warning over the incoming-invoice editor — took both down with one
 * key, because each listened on `document` and each saw the same event.
 * Module-level on purpose: the stack is a fact about the page, not about
 * any one React tree, and portals put every panel on `<body>` anyway.
 */
const OPEN_MODALS: object[] = []

/**
 * Is any {@link Modal} open right now?
 *
 * <p>For surfaces that own their own Escape handling (a full-screen create
 * surface, a peek panel) and sit UNDER a Modal: they should let the key
 * close the modal on top and stay put, the same way two Modals do.
 */
export function hasOpenModal(): boolean {
  return OPEN_MODALS.length > 0
}

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl'

const SIZE: Record<ModalSize, string> = {
  sm: 'max-w-sm p-5',
  md: 'max-w-md p-6',
  lg: 'max-w-lg p-6',
  // Wide enough for an A4 document page (794px at 96dpi) to render at
  // full scale — the invoice preview is unreadable below that.
  xl: 'max-w-4xl p-6',
}

interface ModalProps {
  readonly onClose: () => void
  /** Accessible label for the backdrop close button (localized). */
  readonly closeLabel: string
  /**
   * Optional open state. When provided, the Modal self-manages its
   * presence — render it unconditionally as `<Modal open={x} …/>` and it
   * plays both the enter and the exit animation (staying mounted through
   * the close). When omitted (legacy `{x && <Modal …/>}` call sites), the
   * Modal animates only its entrance and unmounts instantly on close —
   * no behaviour change for existing consumers.
   */
  readonly open?: boolean
  /** id of the in-panel heading, wired to the dialog via aria-labelledby. */
  readonly labelledById?: string
  readonly size?: ModalSize
  /** Extra classes for the panel (rarely needed). */
  readonly className?: string
  /** The modal's own header / body / footer. */
  readonly children: ReactNode
}

/**
 * Centered modal shell — the single owner of the backdrop scrim + blur,
 * the glass panel, and the close affordances (Esc + backdrop click). Every
 * modal renders its own header/body/footer as children, so the scaffolding
 * — and the one blur level (`backdrop-blur-md` over the `bg-bg-overlay`
 * scrim) — lives here and can't drift across modals.
 *
 * The panel is opaque (`bg-glass-base` — a solid hex in every theme, not
 * an rgba fill) per the "popovers/menus must be opaque" rule — only the
 * backdrop is glassy. It used to be `bg-white dark:bg-bg-elevated`, which
 * papered over `--color-bg-elevated` having no light-mode value: light
 * modals rendered flat #FFFFFF, off-palette and jarring against the
 * peek overlay's `bg-glass-base` next to it. `glass-base` is the one
 * surface token defined for all four theme × power combinations, so the
 * panel now tracks lite mode too.
 */
export function Modal({
  onClose,
  closeLabel,
  open,
  labelledById,
  size = 'md',
  className,
  children,
}: ModalProps) {
  // Self-managed when `open` is passed; legacy callers (no `open`) are
  // always "present" while mounted and unmount instantly on close.
  const selfManaged = open !== undefined
  const { mounted, status } = usePresence(selfManaged ? open : true, MODAL_MS)
  const closing = status === 'closing'

  // Identity on the stack. A ref, not the props object — props are
  // remade every render and the stack has to recognise this modal across
  // all of them.
  const token = useRef<object>({})
  const isOpen = selfManaged ? open : true

  // Registered while open, in the order modals opened. Kept apart from
  // the key listener below so a re-render (a fresh `onClose` arrow, say)
  // does not re-register this modal on top of one opened after it.
  useEffect(() => {
    if (!isOpen) return
    const self = token.current
    OPEN_MODALS.push(self)
    return () => {
      const at = OPEN_MODALS.lastIndexOf(self)
      if (at >= 0) OPEN_MODALS.splice(at, 1)
    }
  }, [isOpen])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (OPEN_MODALS[OPEN_MODALS.length - 1] !== token.current) return
      onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  if (!mounted || typeof document === 'undefined') return null

  // Portal to <body> so the `fixed inset-0` overlay is positioned against the
  // viewport, not against a transformed ancestor. Route-transition wrappers
  // (and any element with a `transform`/`filter`) become the containing block
  // for `position: fixed`, which would otherwise center the modal inside the
  // page content instead of the screen.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledById}
      data-state={closing ? 'closing' : 'open'}
      className="fixed inset-0 z-50 grid place-items-center p-4"
    >
      <button
        type="button"
        aria-label={closeLabel}
        onClick={onClose}
        // Scrim: fade only (never animate the blur itself — lite-mode safe).
        className={cn(
          'absolute inset-0 bg-bg-overlay backdrop-blur-md cursor-default',
          closing ? 'animate-fade-out' : 'animate-fade-in',
        )}
      />
      <div
        className={cn(
          'relative w-full rounded-2xl bg-glass-base border border-glass-border shadow-glass',
          // Panel: rise + subtle scale; reduced motion falls back to a fade.
          closing
            ? 'motion-safe:animate-panel-out motion-reduce:animate-fade-out'
            : 'motion-safe:animate-panel-in motion-reduce:animate-fade-in',
          SIZE[size],
          className,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}
